import { Check, X, XCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { cn } from '@/nucleo/utilitarios';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { DetailHeader } from '@/ui/DetailHeader';
import { ErrorState } from '@/ui/ErrorState';
import { FormSection } from '@/ui/FormSection';
import { Card, CardContent } from '@/ui/base/card';
import { Button } from '@/ui/base/button';
import { CardSkeleton } from '@/ui/base/table-skeleton';

import {
  STATUS_PARA_SELO,
  formatarDataHora,
  passosDaLinhaDoTempo,
  podeCancelar,
  rotuloDoTipo,
} from './contrato';
import { useSolicitacao } from './dados';

/**
 * `/credenciado/solicitacoes/:id` — detalhe de uma solicitação de serviço,
 * com a linha do tempo do andamento e a ação de cancelar (quando o status
 * ainda permite).
 *
 * Dado mockado — ver `dados.ts` e `comum/mock.ts` para a justificativa.
 *
 * CANCELAR NÃO GRAVA. Não há API de solicitações: confirmar o cancelamento
 * mostra o aviso de indisponibilidade e o status continua o mesmo. O diálogo
 * diz isso de forma fixa antes da confirmação, porque aviso passageiro some e
 * a captura de tela fica parecendo cancelamento. Mesmo padrão do cancelamento
 * em `financeiro/solicitacoes-sme/SolicitacaoSmeDetalhePage.tsx`.
 */
export function SolicitacaoDetailPage() {
  const { id } = useParams<{ id: string }>();

  const solicitacao = useSolicitacao(id);

  if (solicitacao.isLoading) {
    return (
      <div className="space-y-4" role="status" aria-live="polite">
        <CardSkeleton />
        <CardSkeleton />
        <p className="sr-only">Carregando a solicitação.</p>
      </div>
    );
  }

  if (solicitacao.isError) {
    return (
      <ErrorState
        variant="load-failed"
        description="Não foi possível carregar esta solicitação agora. Tente novamente em instantes."
        primaryAction={{ label: 'Tentar de novo', onClick: () => void solicitacao.refetch() }}
      />
    );
  }

  if (!solicitacao.data) {
    return (
      <ErrorState
        variant="resource-not-found"
        description="Esta solicitação pode ter sido removida ou o endereço está incorreto."
        primaryAction={{ label: 'Voltar para solicitações', to: '/credenciado/solicitacoes' }}
      />
    );
  }

  const item = solicitacao.data;
  const passos = passosDaLinhaDoTempo(item);

  return (
    <div className="space-y-6">
      <DetailHeader
        backTo="/credenciado/solicitacoes"
        backLabel="Solicitações"
        title={`Solicitação ${item.protocolo}`}
        status={{ variant: STATUS_PARA_SELO[item.status], label: item.status }}
        actions={
          podeCancelar(item.status) ? (
            <ConfirmDialog
              trigger={
                <Button variant="destructive" size="sm">
                  <XCircle className="h-4 w-4 mr-1" aria-hidden="true" /> Cancelar solicitação
                </Button>
              }
              title="Cancelar esta solicitação?"
              description="Cancelar solicitações ainda não está disponível. Nada é cancelado nem o DTP é avisado até existir o serviço de solicitações."
              variant="destructive"
              confirmLabel="Cancelar solicitação"
              cancelLabel="Manter solicitação"
              onConfirm={() => mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL)}
            />
          ) : undefined
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <FormSection title="Dados da solicitação">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Tipo</p>
                <p className="text-sm font-medium">{rotuloDoTipo(item.tipo)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Aberta em</p>
                <p className="text-sm font-medium">
                  {new Date(item.aberta_em).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              {item.recurso_afetado_rotulo && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Recurso afetado</p>
                  <p className="text-sm font-medium">{item.recurso_afetado_rotulo}</p>
                </div>
              )}
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground">Motivo</p>
                <p className="text-sm">{item.motivo}</p>
              </div>
              {item.anexo_nome && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-muted-foreground">Anexo</p>
                  <p className="text-sm">{item.anexo_nome}</p>
                </div>
              )}
            </div>
          </FormSection>

          {item.parecer && (
            <FormSection title="Parecer do DTP">
              <p className="text-sm">{item.parecer}</p>
            </FormSection>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold mb-4">Acompanhamento</h3>
            <ol className="space-y-4">
              {passos.map((passo, indice) => (
                <li key={passo.rotulo} className="flex items-start gap-3">
                  <span
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                      passo.negativo
                        ? 'bg-destructive text-destructive-foreground'
                        : passo.concluido
                          ? 'bg-success text-success-foreground'
                          : passo.atual
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground',
                    )}
                    aria-hidden="true"
                  >
                    {passo.negativo ? (
                      <X className="h-3.5 w-3.5" />
                    ) : passo.concluido ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      indice + 1
                    )}
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <p
                      className={cn(
                        'text-sm',
                        passo.atual || passo.concluido || passo.negativo
                          ? 'font-medium text-foreground'
                          : 'text-muted-foreground',
                      )}
                    >
                      {passo.rotulo}
                    </p>
                    {passo.data && (
                      <p className="text-xs text-muted-foreground">
                        {formatarDataHora(passo.data)}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SolicitacaoDetailPage;
